import { useFormik } from "formik";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { Label } from "../Styles/InputStyles";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Images from "../../Images";
import moment from "moment";

const TenantDetailForm = ({ selectedData, postTenantData }) => {
  const validationSchema = Yup.object().shape({
    tenantName: Yup.string().required("Tenant name is required"),
    tenantMobile: Yup.string()
      .matches(/^\d{10}$/, "Enter a valid 10-digit mobile number")
      .required("Phone number is required"),
    tenantGender: Yup.string().required("Gender is required"),
    dob: Yup.date().required("Date of birth is required"),
    fatherName: Yup.string().required("Father's name is required"),
    occupation: Yup.string().required("Occupation is required"),
    propName: Yup.string().required("Property name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    alternateMobile: Yup.string().matches(
      /^\d{10}$/,
      "Enter a valid 10-digit mobile number"
    ),
    address: Yup.string().required("Address is required"),
  });

  const initialValues = {
    tenantName: selectedData?.tenantName || "",
    tenantMobile: selectedData?.tenantMobile || "",
    email: selectedData?.email || "",
    tenantGender: selectedData?.tenantGender || "",
    occupation: selectedData?.occupation || "",
    dob: selectedData?.dob
      ? moment(selectedData?.dob).format("YYYY-MM-DD")
      : "",
    fatherName: selectedData?.fatherName || "",
    propName: selectedData?.propName || "",
    alternateMobile: selectedData?.alternateMobile || "",
    address: selectedData?.address || "",
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      postTenantData(values);
    },
  });

  return (
    <form
      onSubmit={formik.handleSubmit}
      id="room-detail-form"
      className="flex flex-wrap gap-4 p-4 bg-white border rounded shadow-md"
    >
      {/* Tenant Name */}
      <FieldWrapper>
        <Label htmlFor="tenantName">Name</Label>
        <Input
          type="text"
          name="tenantName"
          id="tenantName"
          onChange={formik.handleChange}
          value={formik.values.tenantName}
        />
        <ErrorMessage>
          {formik.touched.tenantName && formik.errors.tenantName}
        </ErrorMessage>
      </FieldWrapper>

      {/* Phone Number */}
      <FieldWrapper>
        <Label htmlFor="tenantMobile">Phone Number</Label>
        <Input
          type="text"
          name="tenantMobile"
          id="tenantMobile"
          onChange={(e) => {
            const { value } = e.target;
            if (/^\d{0,10}$/.test(value)) {
              formik.handleChange(e);
            }
          }}
          value={formik.values.tenantMobile}
        />
        <ErrorMessage>
          {formik.touched.tenantMobile && formik.errors.tenantMobile}
        </ErrorMessage>
      </FieldWrapper>

      {/* Gender */}
      <FieldWrapper>
        <Label htmlFor="tenantGender">Gender</Label>
        <Input
          type="text"
          name="tenantGender"
          id="tenantGender"
          onChange={formik.handleChange}
          value={formik.values.tenantGender}
        />
        <ErrorMessage>
          {formik.touched.tenantGender && formik.errors.tenantGender}
        </ErrorMessage>
      </FieldWrapper>

      {/* Date of Birth */}
      <FieldWrapper>
        <Label htmlFor="dob">Date of Birth</Label>
        <Input
          type="date"
          name="dob"
          id="dob"
          onChange={formik.handleChange}
          value={formik.values.dob}
        />
        <ErrorMessage>{formik.touched.dob && formik.errors.dob}</ErrorMessage>
      </FieldWrapper>

      {/* Father's Name */}
      <FieldWrapper>
        <Label htmlFor="fatherName">Father's Name</Label>
        <Input
          type="text"
          name="fatherName"
          id="fatherName"
          onChange={formik.handleChange}
          value={formik.values.fatherName}
        />
        <ErrorMessage>
          {formik.touched.fatherName && formik.errors.fatherName}
        </ErrorMessage>
      </FieldWrapper>

      {/* Occupation */}
      <FieldWrapper>
        <Label htmlFor="occupation">Occupation</Label>
        <Input
          type="text"
          name="occupation"
          id="occupation"
          onChange={formik.handleChange}
          value={formik.values.occupation}
        />
        <ErrorMessage>
          {formik.touched.occupation && formik.errors.occupation}
        </ErrorMessage>
      </FieldWrapper>

      {/* Property Name */}
      <FieldWrapper>
        <Label htmlFor="propName">Property Name</Label>
        <Input
          type="text"
          name="propName"
          id="propName"
          onChange={formik.handleChange}
          value={formik.values.propName}
        />
        <ErrorMessage>
          {formik.touched.propName && formik.errors.propName}
        </ErrorMessage>
      </FieldWrapper>

      {/* Email */}
      <FieldWrapper>
        <Label htmlFor="email">Email</Label>
        <Input
          type="email"
          name="email"
          id="email"
          onChange={formik.handleChange}
          value={formik.values.email}
        />
        <ErrorMessage>
          {formik.touched.email && formik.errors.email}
        </ErrorMessage>
      </FieldWrapper>

      {/* Alternate Mobile */}
      <FieldWrapper>
        <Label htmlFor="alternateMobile">Alternate Mobile</Label>
        <Input
          type="text"
          name="alternateMobile"
          id="alternateMobile"
          onChange={(e) => {
            const { value } = e.target;
            if (/^\d{0,10}$/.test(value)) {
              formik.handleChange(e);
            }
          }}
          value={formik.values.alternateMobile}
        />
        <ErrorMessage>
          {formik.touched.alternateMobile && formik.errors.alternateMobile}
        </ErrorMessage>
      </FieldWrapper>

      {/* Address */}
      <FieldWrapper>
        <Label htmlFor="address">Address</Label>
        <Textarea
          name="address"
          id="address"
          onChange={formik.handleChange}
          value={formik.values.address}
        />
        <ErrorMessage>
          {formik.touched.address && formik.errors.address}
        </ErrorMessage>
      </FieldWrapper>

      {/* Submit Button */}
      <div className="flex justify-end w-full mt-4">
        <Button type="submit" className="bg-blue-600 text-white">
          Update Details
        </Button>
      </div>
    </form>
  );
};

const Input = tw.input`w-full border border-gray-300 rounded p-2 focus:ring-1 focus:ring-sky-600 focus:outline-none`;
const Textarea = tw.textarea`w-full border border-gray-300 rounded p-2 focus:ring-1 focus:ring-sky-600 focus:outline-none`;
const FieldWrapper = tw.div`w-full sm:w-[48%] space-y-2`;
const ErrorMessage = tw.div`text-red-600 text-sm`;
const Button = tw.button`px-4 py-2 rounded shadow-md`;

export default TenantDetailForm;
